import type { GlobalNaviProduct, Platform } from '../contract/index.js';
import type { MenuItem, ProductMenu } from '../types/menuModel.js';
import { plugin } from './condition.js';
import { QANT_DEBESO_MENU } from './const/debeso.js';
import { QANT_ISSUES_MENU } from './const/issues.js';
import { QANT_KARTE_MENU } from './const/karte.js';
import { QANT_KNOWLEDGE_MENU } from './const/knowledge.js';
import { QANT_DESK_MENU } from './const/knowledgeDesk.js';
import { QANT_NEURO_MENU } from './const/neuro.js';
import { QANT_SMART_FAQ_MENU } from './const/smartFaq.js';
import { QANT_SPEAK_MENU } from './const/speak.js';
import { QANT_TROPPUS_MENU } from './const/supportDataHub.js';
import { QANT_TABULA_MENU } from './const/tabula.js';
import { QANT_VOC_MENU } from './const/voc.js';
import { toMenuData } from './toMenuData.js';
import type { ProductMenuDef } from './type.js';

/**
 * QANT グローバルナビゲーションのプロダクト定義を集約する。
 *
 * - canonical source は `config/const/*.ts` の ProductMenuDef
 * - 本ファイルは集約と UI 向け変換 (`toMenuData`) のみを担う
 *
 * Figma: アプリケーション切り替えメニュー
 *   https://www.figma.com/design/l2Q1DzOhIqidKqXNBwToQy/QANT%E3%82%B0%E3%83%AD%E3%83%8A%E3%83%93?node-id=6225-2791
 */

export const qantProductDefs = {
  PRODUCTS: {
    ISSUES: QANT_ISSUES_MENU,
    SPEAK: QANT_SPEAK_MENU,
    NEURO: QANT_NEURO_MENU,
    VOC: QANT_VOC_MENU,
    KNOWLEDGE: QANT_KNOWLEDGE_MENU,
    DESK: QANT_DESK_MENU,
    SMART_FAQ: QANT_SMART_FAQ_MENU,
  },
  COMMON: {
    TROPPUS: QANT_TROPPUS_MENU,
    TABULA: QANT_TABULA_MENU,
    DEBESO: QANT_DEBESO_MENU,
  },
  LINKED: {
    KARTE: QANT_KARTE_MENU,
  },
} as const satisfies {
  PRODUCTS: { [key: string]: ProductMenuDef };
  COMMON: { [key: string]: ProductMenuDef };
  LINKED: { [key: string]: ProductMenuDef };
};

/**
 * プロダクト一覧フラット配列 (UI / filter 向け)。
 * 順序は PRODUCTS → COMMON → LINKED。
 */
export const qantProductList: readonly ProductMenu[] = [
  toMenuData(qantProductDefs.PRODUCTS.ISSUES),
  toMenuData(qantProductDefs.PRODUCTS.SPEAK),
  toMenuData(qantProductDefs.PRODUCTS.NEURO),
  toMenuData(qantProductDefs.PRODUCTS.VOC),
  toMenuData(qantProductDefs.PRODUCTS.KNOWLEDGE),
  toMenuData(qantProductDefs.PRODUCTS.DESK),
  toMenuData(qantProductDefs.PRODUCTS.SMART_FAQ),
  toMenuData(qantProductDefs.COMMON.TROPPUS),
  toMenuData(qantProductDefs.COMMON.TABULA),
  toMenuData(qantProductDefs.COMMON.DEBESO),
  toMenuData(qantProductDefs.LINKED.KARTE),
];

const withoutKarteConditions = (item: MenuItem): MenuItem => {
  return {
    name: item.name,
    label: item.label,
    ...(item.icon ? { icon: item.icon } : {}),
    ...(item.path ? { path: { ...item.path, host: 'qant' as const } } : {}),
    ...(item.options ? { options: item.options } : {}),
    ...(item.popoverMenus
      ? {
          popoverMenus: item.popoverMenus.map((group) => ({
            ...group,
            items: group.items.map(withoutKarteConditions),
          })),
        }
      : {}),
  };
};

const toQantVocMenuItem = (item: MenuItem): MenuItem => {
  const qantItem = withoutKarteConditions(item);
  return item.name === 'reviewReport' ? { ...qantItem, condition: plugin('voc_review') } : qantItem;
};

export type VocNavigationResolution = {
  /** VoC の L1 が遷移する基盤。migration 設定と target の安全条件を反映する。 */
  mainPlatform: Platform;
  /** VoC の L2 が遷移する基盤。表示中の基盤を優先し、利用不可時だけ反対側へフォールバックする。 */
  menuPlatform: Platform;
  /** 現在の基盤から反対環境へ移る導線の遷移先。 */
  alternatePlatform?: Platform;
  /** 両基盤を利用でき、非 main 側を表示中か。 */
  showEnvironmentBadge: boolean;
  /** enabledProducts に基づく利用状態。permission の accessible とは別概念。 */
  enabled: { karte: boolean; qant: boolean };
  /** productPermissions に基づくアクセス可能状態。 */
  accessible: { karte: boolean; qant: boolean };
};

export type ResolveVocNavigationOptions = {
  selfPlatform: Platform;
  projectIds?: { qant?: string; karte?: string };
};

/**
 * VoC / Speak の enabled / accessible、移行設定、現在の基盤からナビゲーション先を解決する。
 *
 * `products` の entry 有無は enabledProducts の有効状態、`accessible` は product permission
 * の利用可否を表すため、両者を混同しない。L1 は利用可能な基盤だけを使い、両基盤を使える場合だけ
 * main 設定で行き先を選ぶ。Speak の別環境導線とバッジは
 * 両環境を閲覧できる場合だけ出し、main は `useKarteAsMain` が無ければ QANT にする。
 */
const resolveMigrationNavigation = (
  productKey: 'voc' | 'speak',
  products: readonly GlobalNaviProduct[],
  { selfPlatform, projectIds }: ResolveVocNavigationOptions,
): VocNavigationResolution => {
  const karteProduct = products.find((product) => product.key === productKey && product.platform === 'karte');
  const qantProduct = products.find((product) => product.key === productKey && product.platform === 'qant');

  const enabled = { karte: karteProduct !== undefined, qant: qantProduct !== undefined };
  const migration = productKey === 'voc' ? qantProduct?.migration : qantProduct?.speakMigration;
  const accessible = {
    karte: karteProduct?.accessible === true,
    qant: qantProduct?.accessible === true,
  };
  const qantTargetAvailable = enabled.qant && accessible.qant && projectIds?.qant !== undefined;
  const karteTargetAvailable = enabled.karte && accessible.karte && projectIds?.karte !== undefined;
  const mainPlatform: Platform = karteTargetAvailable && (!qantTargetAvailable || migration?.useKarteAsMain === true) ? 'karte' : 'qant';

  const oppositePlatform: Platform = selfPlatform === 'qant' ? 'karte' : 'qant';
  const selfTargetAvailable = selfPlatform === 'qant' ? qantTargetAvailable : karteTargetAvailable;
  const oppositeTargetAvailable = oppositePlatform === 'qant' ? qantTargetAvailable : karteTargetAvailable;
  const menuPlatform: Platform = selfTargetAvailable ? selfPlatform : oppositeTargetAvailable ? oppositePlatform : selfPlatform;

  const alternatePlatform =
    qantTargetAvailable && karteTargetAvailable && (productKey === 'voc' || selfPlatform === mainPlatform) ? oppositePlatform : undefined;

  const showEnvironmentBadge = qantTargetAvailable && karteTargetAvailable && selfPlatform !== mainPlatform;

  return {
    mainPlatform,
    menuPlatform,
    ...(alternatePlatform ? { alternatePlatform } : {}),
    showEnvironmentBadge,
    enabled,
    accessible,
  };
};

/** VoC の移行設定から主導線・サブ導線・環境表示を解決する。 */
export const resolveVocNavigation = (
  products: readonly GlobalNaviProduct[],
  options: ResolveVocNavigationOptions,
): VocNavigationResolution => resolveMigrationNavigation('voc', products, options);

/** Speak の移行設定から主導線・サブ導線・環境表示を解決する。 */
export const resolveSpeakNavigation = (
  products: readonly GlobalNaviProduct[],
  options: ResolveVocNavigationOptions,
): VocNavigationResolution => resolveMigrationNavigation('speak', products, options);

const resolveMigrationMenu = (
  productKey: 'voc' | 'speak',
  products: readonly GlobalNaviProduct[],
  selfPlatform: Platform,
  projectIds: { qant?: string; karte?: string } | undefined,
): ProductMenu => {
  const karteBase = toMenuData(productKey === 'voc' ? QANT_VOC_MENU : QANT_SPEAK_MENU);
  const resolution = resolveMigrationNavigation(productKey, products, { selfPlatform, projectIds });
  const base =
    resolution.menuPlatform === 'qant'
      ? {
          ...karteBase,
          condition: undefined,
          menuGroups: karteBase.menuGroups.map((group) => ({
            ...group,
            menus: group.menus.map(productKey === 'voc' ? toQantVocMenuItem : withoutKarteConditions),
          })),
        }
      : karteBase;

  return {
    ...base,
    landingPath: { host: resolution.mainPlatform === 'qant' ? 'qant' : 'karte-io', path: `/${productKey}/` },
    ...(resolution.alternatePlatform
      ? {
          alternateEnvironmentLink: {
            label:
              resolution.alternatePlatform === 'qant'
                ? { ja: '新環境（QANT）を開く', en: 'Open in QANT' }
                : { ja: '旧環境（KARTE）を開く', en: 'Open in KARTE' },
            path: { host: resolution.alternatePlatform === 'qant' ? 'qant' : 'karte-io', path: `/${productKey}/` },
          },
        }
      : {}),
  };
};

/** VoC / Speak の移行設定と現在の基盤に応じたメニューを返す。 */
export const resolveQantProductList = (
  products: readonly GlobalNaviProduct[],
  options?: { selfPlatform?: Platform; projectIds?: { qant?: string; karte?: string } },
): readonly ProductMenu[] => {
  return qantProductList.map((product) => {
    if (product.name === 'voc' || product.name === 'speak')
      return resolveMigrationMenu(product.name, products, options?.selfPlatform ?? 'karte', options?.projectIds);
    return product;
  });
};
